import { Card, CardContent, CardHeader, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Theme } from '@material-ui/core/styles';
import { useDropzone } from 'react-dropzone';
import { Box } from '@material-ui/core';
import { Close, CloudDownload, CloudUpload } from '@material-ui/icons';
import { FC, SyntheticEvent, useEffect, useMemo } from 'react';
import {
  Button,
  createDateField,
  createSelectField,
  createTextField,
  IconButton,
} from '@roc/ui';
import Grid from '@material-ui/core/Grid';
import { idTypeOptions } from '@roc/feature-loans';
import {
  NO,
  YES,
  borrowerStatesAbbreviation,
  borrowerStatesNames,
  isNotBlank,
} from '@roc/feature-utils';
import { observer } from 'mobx-react';
import { format, isValid } from 'date-fns';
import { useStore } from '@roc/client-portal-shared/hooks';
import { PersonalInformation } from './personalInformation';
import { useUserStore } from '@roc/feature-app-core';

const useFileUploadStyles = makeStyles((theme: Theme) => ({
  card: {
    marginTop: theme.spacing(2),
  },
  cardContent: {
    paddingTop: 0,
  },
  fileLine: {
    display: 'flex',
    alignItems: 'center',
  },
  dragArea: {
    border: '2px dotted #427ED1',
    width: '100%',
    backgroundColor: '#EAF4FA',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    padding: 20,
    borderRadius: 5,
    cursor: 'pointer',
  },
  icon: {
    minWidth: '20px',
    minHeight: '20px',
    width: '3vw',
    height: '3vw',
    color: '#4B89DA',
  },
  fileContainer: {
    border: '1px solid grey',
    backgroundColor: '#EAF4FA',
    padding: '10px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: '10px 0',
    borderRadius: 5,
  },

  fileName: {
    marginLeft: 5,
    marginRight: 2,
    flexGrow: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  removeButton: {
    marginLeft: theme.spacing(2),
    alignSelf: 'flex-end',
    alignItems: 'flex-end',
    float: 'right',
  },
}));

const stateOptions = borrowerStatesAbbreviation.map((abbreviation, i) => ({
  value: abbreviation,
  label: borrowerStatesNames[i],
}));

export const IdInformationStep = observer(({ displayPersonalInformation = false }: { displayPersonalInformation?: boolean }) => {
  const { identityCheckStore } = useStore();
  const { idInformationForm } = identityCheckStore;
  const { userStore } = useUserStore();
  const { isNportalUser } = userStore;

  const showPassportFields = useMemo(() => {
    return (
      idInformationForm.form.fields.idType.value !==
      "US Driver's License / Government ID"
    );
  }, [idInformationForm.form.fields.idType.value]);

  const uploadImageTitle = useMemo(() => {
    if (!displayPersonalInformation) return "Download Driver's License Image";
    if (showPassportFields) return 'Document Upload - Document ID Image*';
    return "Document Upload - Driver's License Image*";
  }, [showPassportFields]);

  useEffect(() => {
    idInformationForm.onFieldChange(
      'driversLicense',
      showPassportFields ? NO : YES
    );
  }, [showPassportFields]);

  const manageDate = (fieldName, date) => {
    let formattedDate = null;
    if (isValid(date)) {
      formattedDate = format(date, 'MM/dd/yyyy');
    }
    idInformationForm.onFieldChange(fieldName, formattedDate);
  };

  const handleDownload = () => {
    identityCheckStore.downloadDLFile();
  }

  return (
    <Grid container spacing={2}>
      {displayPersonalInformation && <PersonalInformation personalInformation={identityCheckStore.formInfo} />}
      <Grid item xs={12}>
        <Typography variant="h5">Identification</Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        {createSelectField({
          store: idInformationForm,
          testId: 'idType',
          fieldName: 'idType',
          label: 'ID Type',
          options: idTypeOptions,
          placeholder: 'Make Selection',
          disabled: !(displayPersonalInformation || isNportalUser)
        })}
      </Grid>
      {showPassportFields ? (
        <>
          <Grid item xs={12} sm={3}>
            {createTextField({
              store: idInformationForm,
              testId: 'passportNumber',
              fieldName: 'passportNumber',
              label: 'Passport Number',
            })}
          </Grid>
          <Grid item xs={12} sm={3}>
            {createDateField({
              store: idInformationForm,
              label: 'Passport Expiration',
              fieldName: 'passportExpiration',
              testId: 'passportExpiration',
              format: 'MM/dd/yyyy',
              onChange: manageDate,
            })}
          </Grid>
        </>
      ) : (
        <>
          <Grid item xs={12} sm={3}>
            {createSelectField({
              store: idInformationForm,
              testId: 'driversLicenseState',
              fieldName: 'driversLicenseState',
              label: "Driver's License State",
              options: stateOptions,
              disabled: !(displayPersonalInformation || isNportalUser)
            })}
          </Grid>
          <Grid item xs={12} sm={3}>
            {createTextField({
              store: idInformationForm,
              testId: 'driversLicenseNumber',
              fieldName: 'driversLicenseNumber',
              label: "Driver's License Number",
              disabled: !(displayPersonalInformation || isNportalUser)
            })}
          </Grid>
        </>
      )}
      <Grid item xs={12}>
        <DriversLicenseUpload
          displayPersonalInformation={displayPersonalInformation}
          title={uploadImageTitle}
          file={identityCheckStore.driversLicense}
          onChange={file => identityCheckStore.setDriversLicense(file)}
          onDownload={identityCheckStore.formInfo?.driversLicensePath ? handleDownload : null}
          isNportalUser={isNportalUser}
        />
      </Grid>
    </Grid>
  );
});

interface DriversLicenseUploadProps {
  displayPersonalInformation: boolean;
  title?: string;
  file: File | null;
  onChange: (file: File | null) => void;
  onDownload: () => void;
  isNportalUser: boolean;
}

export const DriversLicenseUpload: FC<DriversLicenseUploadProps> = ({
  displayPersonalInformation,
  title = 'Document Upload - Passport Image*',
  file,
  onChange,
  onDownload,
  isNportalUser,
}) => {
  function onDrop(newFiles: File[]) {
    onChange(newFiles?.[0]);
  }

  function handleOnRemove(event: SyntheticEvent) {
    event.stopPropagation();
    onChange(null);
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
  });

  const classes = useFileUploadStyles();
  return (
    <Card variant="outlined" className={classes.card}>
      <CardHeader
        title={title}
        action={
          onDownload && (
            <IconButton
              color="primary"
              testId="download"
              onClick={onDownload}
              size="small"
            >
              <CloudDownload />
            </IconButton>
          )
        }
      />
      {(isNportalUser || displayPersonalInformation) && <CardContent className={classes.cardContent}>
        <Box {...getRootProps()} className={classes.dragArea}>
          <input {...getInputProps()} />
          <CloudUpload className={classes.icon} />
          {file ? (
            <Box className={classes.fileLine}>
              <Typography>{file?.name}</Typography>
              <Button
                testId={`removeFile-${file?.name}`}
                size="small"
                variant="outlined"
                className={classes.removeButton}
                onClick={handleOnRemove}
              >
                REMOVE <Close />
              </Button>
            </Box>
          ) : isDragActive ? (
            <p>Drop the file(s) here...</p>
          ) : (
            <>
              <p>Drag File to Upload</p>
              <p>or Browse file</p>
            </>
          )}
        </Box>
        <Box></Box>
      </CardContent>}
    </Card>
  );
};
